import classNames from 'classnames';
import type { ReactNode, Ref } from 'react';
import styled, { keyframes } from 'styled-components';

import { unitMapping, type Unit } from '#src/lib/style.ts';

const Root = styled.span`
  isolation: isolate;
  position: relative;
  display: inline-flex;
  vertical-align: middle;
  flex-shrink: 0;
`;

const borderPulseAnimation = keyframes`
  0% {
    transform: scale(1);
    opacity: 1;

  }
  50% {
    transform: scale(1.5);
    opacity: 0;
  }
  100% {
    opacity: 0;
  }
`;

const enterAnimation = keyframes`
from {
  transform: translate(50%, -50%) scale(0);
  opacity: 0;
}
to {
  transform: translate(50%, -50%) scale(1);
  opacity: 1;
}
`;

const AbsoluteSpan = styled.span`
  position: absolute;
  top: 0;
  right: 0;
  display: flex;
  flex-direction: row;
  flex-wrap: wrap;
  justify-content: center;
  align-content: center;
  z-index: 1;
  border-radius: var(--intermediate-unit);
  color: var(--c-onBase-primary);
  background-color: var(--c-red-400);

  min-width: var(--double-and-a-half-unit);
  height: var(--double-and-a-half-unit);
  &.medium {
    min-width: var(--double-and-a-half-unit);
    height: var(--double-and-a-half-unit);
    font: var(--t-bold) var(--t-label-s);
  }
  &.small {
    min-width: var(--intermediate-unit);
    height: var(--intermediate-unit);
    font: var(--t-bold) var(--t-label-xs);
  }

  &.circularOverlap {
    top: 14%;
    right: 14%;
  }

  &.dot {
    min-width: var(--unit);
    height: var(--unit);
  }

  &.collapsed {
    transform: translate(50%, -50%) scale(0);
  }

  &.blue {
    background-color: var(--c-blue-400);
  }

  &.grey {
    background-color: var(--c-surface-opaque);
    color: var(--c-nd-900);
  }
  &.red:not(.collapsed):not(:hover):not(:active) {
    &:after {
      content: '';
      position: absolute;
      inset: 0;
      border: 1px solid var(--c-red-400);
      border-radius: var(--intermediate-unit);
      animation: ${borderPulseAnimation} 1.33s 2s
        cubic-bezier(0.03, 0.42, 0.47, 0.97) infinite;
    }
    .active &:after {
      animation: none;
    }
  }
  transform: translate(50%, -50%) scale(0);
  opacity: 0;
  &:not(.collapsed) {
    animation: ${enterAnimation} 0.4s 0.5s ease-in-out forwards;
  }
`;

type Props = {
  children?: ReactNode;
  variant?: 'dot' | 'standard';
  overlap?: 'circular' | 'rectangular';
  badgeContent?: ReactNode;
  invisible?: boolean;
  className?: string;
  top?: Unit;
  right?: Unit;
  color?: 'red' | 'blue' | 'grey';
  'aria-label'?: string;
  size?: 'small' | 'medium';
  ref?: Ref<HTMLElement>;
};
export const Badge = (props: Props) => {
  const {
    children,
    variant = 'standard',
    overlap = 'rectangular',
    badgeContent,
    invisible,
    className,
    top,
    right,
    color = 'red',
    'aria-label': ariaLabel,
    size = 'medium',
    ref,
  } = props;
  const hideBadge = invisible || (!badgeContent && variant !== 'dot');

  const content =
    typeof badgeContent === 'number' && badgeContent > 99
      ? '99+'
      : badgeContent;

  return (
    <Root className={className} aria-label={ariaLabel} ref={ref}>
      {children}
      <AbsoluteSpan
        className={classNames(size, color, {
          circularOverlap: overlap === 'circular',
          dot: variant === 'dot',
          collapsed: hideBadge,
        })}
        style={{
          top: top ? unitMapping[top] : undefined,
          right: right ? unitMapping[right] : undefined,
        }}
      >
        {content}
      </AbsoluteSpan>
    </Root>
  );
};
